import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useAuth } from '@/features/auth/AuthContext';

export interface Period {
  id: string;
  semester: number;
  programYear: number;
  phase: string | null;
  name: string;
  description: string | null;
  status: 'upcoming' | 'active' | 'locked';
}

interface PeriodState {
  periods: Period[];
  current: Period | null;
  currentId: string | null;
  isAll: boolean;
  setCurrentId: (id: string) => void;
  loading: boolean;
}

const Ctx = createContext<PeriodState | null>(null);

const STORAGE_KEY = 'lentera.periodId';

/**
 * Periode aktif mengunci konteks seluruh halaman yang relevan.
 */
export function PeriodProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [currentId, setCurrentIdState] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY) ?? null,
  );

  const { data, isLoading } = useQuery({
    queryKey: ['periods', user?.id],
    queryFn: () => api.get<Period[]>('/periods'),
    enabled: Boolean(user),
    staleTime: 5 * 60 * 1000,
  });

  const periods = useMemo(() => data ?? [], [data]);

  // Default ke periode berstatus AKTIF kalau pengguna belum pernah memilih,
  // atau reset jika ID yang tersimpan di localStorage sudah tidak ada di daftar.
  useEffect(() => {
    if (periods.length === 0) return;
    if (!currentId || (currentId !== 'all' && !periods.some((p) => p.id === currentId))) {
      const active = periods.find((p) => p.status === 'active') ?? periods[0];
      if (active) {
        setCurrentIdState(active.id);
        localStorage.setItem(STORAGE_KEY, active.id);
      }
    }
  }, [periods, currentId]);

  const setCurrentId = (id: string) => {
    setCurrentIdState(id);
    localStorage.setItem(STORAGE_KEY, id);
  };

  const isAll = currentId === 'all';
  const current = isAll ? null : (periods.find((p) => p.id === currentId) ?? null);

  return (
    <Ctx.Provider
      value={{
        periods,
        current,
        currentId: currentId ?? (current?.id ?? 'all'),
        isAll,
        setCurrentId,
        loading: isLoading,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function usePeriod(): PeriodState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('usePeriod harus dipakai di dalam <PeriodProvider>');
  return ctx;
}

export function PeriodSwitcher() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { periods, current, currentId, setCurrentId, loading } = usePeriod();

  // Pilihan A: Sembunyikan bila bukan di Beranda (/), Pengukuran (/pengukuran), atau Monev (/monev)
  const isPeriodRelevant =
    location.pathname === '/' ||
    location.pathname.startsWith('/pengukuran') ||
    location.pathname.startsWith('/monev');

  if (!isPeriodRelevant) return null;

  if (loading) return <div className="h-10 w-44 animate-pulse rounded-full bg-app" aria-hidden />;
  if (periods.length === 0) return null;

  const isAdmin = user?.role === 'super_admin';

  return (
    <div className="flex items-center gap-2">
      <label
        className="flex min-w-0 items-center gap-2"
        title={
          current?.description
            ? `${current.name} — ${current.description}`
            : current
              ? current.name
              : 'Menampilkan data dari semua semester'
        }
      >
        <span className="sr-only">Periode evaluasi</span>
        <select
          value={currentId ?? 'all'}
          onChange={(e) => setCurrentId(e.target.value)}
          className={cn(
            'min-h-10 min-w-0 max-w-[46vw] truncate rounded-full border border-border bg-surface px-4 pr-8 sm:max-w-none',
            'text-sm font-medium text-ink cursor-pointer hover:border-ink/30 transition-colors',
          )}
        >
          <option value="all">Semua Semester</option>
          {periods.map((p) => (
            <option key={p.id} value={p.id}>
              Semester {p.semester}{p.status === 'active' ? ' (Aktif)' : ''}
            </option>
          ))}
        </select>
      </label>

      {isAdmin && (
        <button
          type="button"
          onClick={() => navigate('/pengaturan/periode')}
          title="Kelola & Atur Periode (CRUD)"
          className="inline-flex h-9 items-center gap-1.5 rounded-full border border-border bg-surface px-3 text-xs font-medium text-ink-secondary hover:bg-app hover:text-ink transition-colors cursor-pointer"
        >
          <span>⚙️</span>
          <span className="hidden sm:inline">Kelola Periode</span>
        </button>
      )}
    </div>
  );
}
